<template>
    <form autocomplete="off">
        <div class="row gy-3">
            <div class="col-lg-12">
                <label>Employer name or Company name <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.employer" placeholder="Enter employer"
                    :class="{ 'is-invalid': v$.form.employer.$error }">

                <div class="invalid-feedback" v-for="(item, index) in v$.form.employer.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-lg-6">
                <label>Position <span class="text-danger">*</span></label>
                <input type="text" class="form-control" v-model="form.position" placeholder="Enter position"
                    :class="{ 'is-invalid': v$.form.position.$error }">

                <div class="invalid-feedback" v-for="(item, index) in v$.form.position.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>

            <div class="col-lg-6">
                <label>Grade Level</label>
                <input type="text" class="form-control" placeholder="Enter grade_level" v-model="form.grade_level">
            </div>
            <div class="col-lg-6">
                <label>Nature of Appointment <span class="text-danger">*</span></label>
                <select class="form-select" v-model="form.appointment_type"
                    :class="{ 'is-invalid': v$.form.appointment_type.$error }">
                    <option disabled value="">--select--</option>
                    <option v-for="(item, index) in appointmentTypes" :key="index" :value="item.value">
                        {{ item.label }}
                    </option>
                </select>
                <div class="invalid-feedback" v-for="(item, index) in v$.form.appointment_type.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-lg-6">
                <label for="education-status" class="form-label">Status <span class="text-danger">*</span></label>
                <select v-model="form.status" class="form-select" @change="form.end_date = ''"
                    :class="{ 'is-invalid': v$.form.status.$error }">
                    <option disabled value="">--select status--</option>
                    <option v-for="(option, stIndex) in statusOptions" :key="stIndex" :value="option.value">
                        {{ option.label }}</option>
                </select>
                <div v-for="(item, index) in v$.form.status.$errors" :key="index" class="invalid-feedback">
                    <span v-if="item.$message">{{ item.$message }}</span>
                </div>
            </div>
            <div class="col-md-6">
                <label class="form-label">
                    Start Date <span class="text-danger">*</span>
                </label>
                <div>
                    <CommonDatePicker v-model="form.start_date" :maxDate="new Date()"
                        :error="v$.form.start_date.$error" />
                </div>
                <div v-for="(item, index) in v$.form.start_date.$errors" :key="index" class="text-danger small">
                    <span v-if="item.$message">{{ item.$message }}</span>
                </div>
            </div>
            <div class="col-md-6">
                <label class="form-label">
                    End Date
                </label>
                <div>
                    <CommonDatePicker :disabled="form.status == 'current'" v-model="form.end_date"
                        :error="v$.form.end_date.$error" :maxDate="new Date()" />
                </div>
                <div v-for="(item, index) in v$.form.end_date.$errors" :key="index" class="text-danger small">
                    <span v-if="item.$message">{{ item.$message }}</span>
                </div>
            </div>
            <div class="col-12">
                <label>Responsibilities <span class="text-danger">*</span></label>
                <textarea rows="5" class="form-control" v-model="form.responsibilities"
                placeholder="Describe your primary responsibilities, achievements, and duties in this position."
                    :class="{ 'is-invalid': v$.form.responsibilities.$error }"></textarea>

                <div class="invalid-feedback" v-for="(item, index) in v$.form.responsibilities.$errors" :key="index">
                    {{ item.$message }}
                </div>
            </div>
            <div class="col-12">
                <div class="d-flex justify-content-end mt-3">
                    <b-button variant="primary" type="button" @click.prevent="editMode ? update() : create()">
                        {{ editMode ? 'Update' : 'Submit' }}
                    </b-button>
                </div>
            </div>
        </div>
    </form>
</template>

<script>
import { required, helpers, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { appointmentTypes } from "@shared/constants/common.js"

export default {
    setup() {
        return { v$: useVuelidate() };
    },
    props: {
        editItem: {
            type: Object,
            default: null
        },
        editMode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            form: {
                employer: "",
                position: "",
                grade_level: "",
                appointment_type: "",
                status: "",
                start_date: "",
                end_date: "",
                responsibilities: "",
            },
            statusOptions: [
                { value: "current", label: "Current" },
                { value: "completed", label: "Completed" },
                { value: "resigned", label: "Resigned" },
                { value: "retired", label: "Retired" },
                { value: "terminated", label: "Terminated" },
                { value: "contract_ended", label: "Contract Ended" },
                { value: "dismissed", label: "Dismissed" }
            ],
            appointmentTypes: appointmentTypes,
        }
    },
    validations() {
        return {
            form: {
                employer: {
                    required: helpers.withMessage("Employer is required", required)
                },
                position: {
                    required: helpers.withMessage("Position is required", required)
                },
                status: {
                    required: helpers.withMessage("Status is required", required)
                },
                appointment_type: {
                    required: helpers.withMessage("Nature of appointment is required", required)
                },
                start_date: {
                    required: helpers.withMessage("Start date is required", required)
                },
                end_date: {
                    required: helpers.withMessage("End date is required",
                        requiredIf(() => this.form.status !== "current")
                    )
                },
                responsibilities: {
                    required: helpers.withMessage("Responsibilities are required", required)
                }
            }
        }
    },
    methods: {
        create() {
            this.v$.$validate().then(result => {
                if (!result) {
                    this.alertError(this.v$.$errors[0].$message);
                    return;
                }
                this.$store.dispatch("changeLoaderValue", true);
                this.$http.post('/employment-histories/create', this.form)
                    .then(response => {
                        this.$store.dispatch("changeLoaderValue", false);
                        if (response.data.success) {
                            this.$emit("addedItem", response.data.data);
                            this.resetForm();
                        }
                    });
            });
        },
        update() {
            this.v$.$validate().then(result => {
                if (!result) {
                    this.alertError(this.v$.$errors[0].$message);
                    return;
                }
                this.$store.dispatch("changeLoaderValue", true);
                this.$http.post(`/employment-histories/${this.editItem.id}/update`, this.form)
                    .then(response => {
                        this.$store.dispatch("changeLoaderValue", false);
                        if (response.data.success) {
                            this.$emit("updatedItem", response.data.data);
                            this.resetForm();
                        }
                    });
            });
        },
        resetForm() {
            this.v$.$reset();
            this.$emit("closeMe");
        }
    },
    created() {
        if (this.editMode) {
            this.form = this.$filters.patchUpdatables(this.form, this.editItem);
        }
    }

}
</script>